import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { User, Mail, Phone, Calendar, MapPin, Edit, Save, Lock } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Profil() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    nickname: '',
    phone: '',
    bio: '',
    address: '',
    birth_date: '',
    profile_image: ''
  });
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [passwordData, setPasswordData] = useState({ current_password: '', new_password: '', new_password2: '' });
  const [pwLoading, setPwLoading] = useState(false);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (passwordData.new_password.length < 6) {
      toast.error('Yeni şifre en az 6 karakter olmalı'); return;
    }
    if (passwordData.new_password !== passwordData.new_password2) {
      toast.error('Yeni şifreler eşleşmiyor'); return;
    }
    setPwLoading(true);
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${BACKEND_URL}/api/auth/password`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ current_password: passwordData.current_password, new_password: passwordData.new_password }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || 'Şifre güncellenemedi');
      }
      toast.success('Şifreniz güncellendi');
      setShowPasswordForm(false);
      setPasswordData({ current_password: '', new_password: '', new_password2: '' });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setPwLoading(false);
    }
  };

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setUser(data);
        setFormData({
          nickname: data.nickname || '',
          phone: data.phone || '',
          bio: data.bio || '',
          address: data.address || '',
          birth_date: data.birth_date || '',
          profile_image: data.profile_image || ''
        });
      }
    } catch (error) {
      toast.error('Profil yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/auth/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        const data = await response.json();
        setUser(data);
        setEditMode(false);
        toast.success('Profil güncellendi');
      } else {
        toast.error('Güncelleme başarısız');
      }
    } catch (error) {
      toast.error('Bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Belirtilmemiş';
    const date = new Date(dateString);
    return date.toLocaleDateString('tr-TR', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric'
    });
  };

  if (loading && !user) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white flex items-center justify-center">
        <div className="text-xl text-amber-800">Yükleniyor...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8 fade-in">
          <h1 className="text-4xl sm:text-5xl font-bold text-amber-900 mb-4" style={{ fontFamily: 'Merriweather, serif' }}>
            Profilim
          </h1>
          <p className="text-lg text-amber-700">Kişisel bilgilerinizi görüntüleyin ve düzenleyin</p>
        </div>

        <Card className="border-2 border-amber-200 shadow-xl">
          <CardHeader className="bg-gradient-to-r from-amber-100 to-orange-100">
            <div className="flex items-center justify-between">
              <CardTitle className="text-2xl text-amber-900 flex items-center gap-2">
                <User className="w-6 h-6" />
                Profil Bilgileri
              </CardTitle>
              {!editMode && (
                <Button
                  onClick={() => setEditMode(true)}
                  className="bg-amber-600 hover:bg-amber-700"
                  data-testid="edit-profile-btn"
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Düzenle
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="pt-6">
            {editMode ? (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="flex justify-center mb-6">
                  {formData.profile_image ? (
                    <div className="relative">
                      <img 
                        src={formData.profile_image} 
                        alt="Profil"
                        className="w-32 h-32 rounded-full object-cover border-4 border-amber-300"
                      />
                    </div>
                  ) : (
                    <div className="w-32 h-32 rounded-full bg-gradient-to-br from-amber-600 to-orange-600 flex items-center justify-center text-white text-4xl font-bold border-4 border-amber-300">
                      {formData.nickname.charAt(0).toUpperCase()}
                    </div>
                  )}
                </div>

                <div>
                  <Label htmlFor="profile-image">Profil Resmi URL</Label>
                  <Input
                    id="profile-image"
                    value={formData.profile_image}
                    onChange={(e) => setFormData({...formData, profile_image: e.target.value})}
                    placeholder="https://example.com/photo.jpg"
                    className="border-amber-300 focus:border-amber-500"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="nickname">Kullanıcı Adı *</Label>
                    <Input
                      id="nickname"
                      value={formData.nickname}
                      onChange={(e) => setFormData({...formData, nickname: e.target.value})}
                      required
                      className="border-amber-300 focus:border-amber-500"
                      data-testid="nickname-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="phone">Telefon *</Label>
                    <Input
                      id="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      required
                      className="border-amber-300 focus:border-amber-500"
                      data-testid="phone-input"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="bio">Hakkımda</Label>
                  <Textarea
                    id="bio"
                    value={formData.bio}
                    onChange={(e) => setFormData({...formData, bio: e.target.value})}
                    rows={4}
                    placeholder="Kendiniz hakkında kısa bir bilgi..."
                    className="border-amber-300 focus:border-amber-500"
                    data-testid="bio-input"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="birth-date">Doğum Tarihi</Label>
                    <Input
                      id="birth-date"
                      type="date"
                      value={formData.birth_date}
                      onChange={(e) => setFormData({...formData, birth_date: e.target.value})}
                      className="border-amber-300 focus:border-amber-500"
                      data-testid="birth-date-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="address">Adres</Label>
                    <Input
                      id="address"
                      value={formData.address}
                      onChange={(e) => setFormData({...formData, address: e.target.value})}
                      placeholder="Şehir, İlçe"
                      className="border-amber-300 focus:border-amber-500"
                      data-testid="address-input"
                    />
                  </div>
                </div>

                <div className="flex gap-4">
                  <Button 
                    type="submit" 
                    disabled={loading}
                    className="bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700"
                    data-testid="save-profile-btn"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {loading ? 'Kaydediliyor...' : 'Kaydet'}
                  </Button>
                  <Button
                    type="button"
                    onClick={() => {
                      setEditMode(false);
                      setFormData({
                        nickname: user.nickname || '',
                        phone: user.phone || '',
                        bio: user.bio || '',
                        address: user.address || '',
                        birth_date: user.birth_date || '',
                        profile_image: user.profile_image || ''
                      });
                    }}
                    variant="outline"
                    className="border-amber-300 text-amber-900"
                  >
                    İptal
                  </Button>
                </div>
              </form>
            ) : (
              <div className="space-y-6">
                <div className="flex justify-center mb-6">
                  {user?.profile_image ? (
                    <img 
                      src={user.profile_image} 
                      alt="Profil"
                      className="w-32 h-32 rounded-full object-cover border-4 border-amber-300 shadow-lg"
                    />
                  ) : (
                    <div className="w-32 h-32 rounded-full bg-gradient-to-br from-amber-600 to-orange-600 flex items-center justify-center text-white text-4xl font-bold border-4 border-amber-300 shadow-lg">
                      {user?.nickname.charAt(0).toUpperCase()}
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-amber-600">
                      <User className="w-5 h-5" />
                      <span className="font-semibold">Kullanıcı Adı</span>
                    </div>
                    <p className="text-amber-900 text-lg pl-7">{user?.nickname}</p>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-amber-600">
                      <Mail className="w-5 h-5" />
                      <span className="font-semibold">E-posta</span>
                    </div>
                    <p className="text-amber-900 text-lg pl-7">{user?.email}</p>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-amber-600">
                      <Phone className="w-5 h-5" />
                      <span className="font-semibold">Telefon</span>
                    </div>
                    <p className="text-amber-900 text-lg pl-7">{user?.phone}</p>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-amber-600">
                      <Calendar className="w-5 h-5" />
                      <span className="font-semibold">Doğum Tarihi</span>
                    </div>
                    <p className="text-amber-900 text-lg pl-7">{formatDate(user?.birth_date)}</p>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-amber-600">
                      <MapPin className="w-5 h-5" />
                      <span className="font-semibold">Adres</span>
                    </div>
                    <p className="text-amber-900 text-lg pl-7">{user?.address || 'Belirtilmemiş'}</p>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-amber-600">
                      <Calendar className="w-5 h-5" />
                      <span className="font-semibold">Üyelik Tarihi</span>
                    </div>
                    <p className="text-amber-900 text-lg pl-7">{formatDate(user?.created_at)}</p>
                  </div>
                </div>

                {user?.bio && (
                  <div className="pt-6 border-t border-amber-200">
                    <h3 className="text-amber-900 font-semibold mb-3">Hakkımda</h3>
                    <p className="text-amber-800 leading-relaxed whitespace-pre-wrap">{user.bio}</p>
                  </div>
                )}

                <div className="pt-6 border-t border-amber-200">
                  <div className="flex items-center gap-2 text-amber-700 mb-5">
                    <div className={`w-3 h-3 rounded-full ${user?.role === 'admin' ? 'bg-orange-600' : 'bg-green-600'}`}></div>
                    <span className="font-semibold">
                      {user?.role === 'admin' ? 'Yönetici' : user?.role === 'editor' ? 'Editör' : 'Üye'}
                    </span>
                  </div>

                  {/* Password change section */}
                  {!showPasswordForm ? (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowPasswordForm(true)}
                      className="border-amber-300 text-amber-800 hover:bg-amber-50"
                      data-testid="show-password-form-btn"
                    >
                      <Lock className="w-4 h-4 mr-2" />Şifremi Değiştir
                    </Button>
                  ) : (
                    <form onSubmit={handleChangePassword} className="bg-amber-50/50 border border-amber-200 rounded-2xl p-5 space-y-3" data-testid="password-change-form">
                      <div className="flex items-center gap-2 mb-2">
                        <Lock className="w-5 h-5 text-amber-700" />
                        <h3 className="text-amber-900 font-semibold">Şifre Güncelle</h3>
                      </div>
                      <div>
                        <Label className="text-amber-800">Mevcut Şifre</Label>
                        <Input
                          type="password"
                          required
                          value={passwordData.current_password}
                          onChange={e => setPasswordData({...passwordData, current_password: e.target.value})}
                          className="border-amber-300"
                          data-testid="current-password-input"
                        />
                      </div>
                      <div>
                        <Label className="text-amber-800">Yeni Şifre</Label>
                        <Input
                          type="password"
                          required
                          minLength={6}
                          value={passwordData.new_password}
                          onChange={e => setPasswordData({...passwordData, new_password: e.target.value})}
                          className="border-amber-300"
                          data-testid="new-password-input"
                        />
                        <p className="text-xs text-amber-600 mt-1">En az 6 karakter</p>
                      </div>
                      <div>
                        <Label className="text-amber-800">Yeni Şifre (Tekrar)</Label>
                        <Input
                          type="password"
                          required
                          minLength={6}
                          value={passwordData.new_password2}
                          onChange={e => setPasswordData({...passwordData, new_password2: e.target.value})}
                          className="border-amber-300"
                          data-testid="new-password2-input"
                        />
                      </div>
                      <div className="flex gap-2 pt-1">
                        <Button type="submit" disabled={pwLoading} className="bg-gradient-to-r from-amber-600 to-orange-600 text-white hover:from-amber-700 hover:to-orange-700" data-testid="submit-password-change">
                          {pwLoading ? 'Güncelleniyor...' : 'Güncelle'}
                        </Button>
                        <Button type="button" variant="outline" onClick={() => { setShowPasswordForm(false); setPasswordData({ current_password: '', new_password: '', new_password2: '' }); }}>
                          İptal
                        </Button>
                      </div>
                    </form>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
